import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';
import { 
  scoreDimensions, 
  calculateTotalScore, 
  getScoreLevel, 
  getRecommendations 
} from '../data/workEnvironmentScoreLogic';

export const WorkEnvironmentScore = () => {
  const [scores, setScores] = useState({
    leadership: 52,
    roleClarity: 48,
    collaboration: 65,
    psychSafety: 42,
    workload: 38,
    stress: 35,
    conflict: 55,
    culture: 60,
    engagement: 58
  });

  const totalScore = useMemo(() => calculateTotalScore(scores), [scores]);
  const level = useMemo(() => getScoreLevel(totalScore), [totalScore]);
  const recommendations = useMemo(() => getRecommendations(scores), [scores]);

  const trendOption = {
    tooltip: { trigger: 'axis' },
    xAxis: { type: 'category', data: ['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun'], axisLine: { lineStyle: { color: '#e2e8f0' } } },
    yAxis: { type: 'value', max: 100 },
    series: [{
      data: [62, 58, 55, 50, 48, totalScore],
      type: 'line',
      smooth: true,
      color: '#4f46e5',
      areaStyle: { opacity: 0.1 }
    }]
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Work Environment Score</h1>
          <p className="text-slate-500 mt-1">Samlet vurdering av virksomhetens psykososiale tilstand.</p>
        </div>
        <Button variant="primary" icon={<SafeIcon name="Download" />}>
          Eksporter Rapport
        </Button>
      </div>

      {/* Hero Score Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className={`bg-slate-900 border-none text-white overflow-hidden relative lg:col-span-1`}>
          <div className={`absolute top-0 right-0 w-32 h-full bg-${level.color}-500/10 blur-3xl`}></div>
          <CardContent className="p-10 text-center relative z-10 flex flex-col justify-center h-full">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Total Score</p>
            <div className={`text-8xl font-black text-${level.color}-400 mb-4`}>{totalScore}</div>
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-${level.color}-400 text-xs font-bold uppercase mx-auto`}>
              <SafeIcon name="Activity" /> {level.label}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader title="Trendanalyse" subtitle="Utvikling av totalscore siste 6 måneder" icon={<SafeIcon name="TrendingUp" />} />
          <CardContent>
            <div className="h-64">
              <ReactECharts option={trendOption} style={{ height: '100%' }} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dimensions Grid */}
      <div className="space-y-4">
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider ml-1">Dimensjons-score</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {scoreDimensions.map((dim) => (
            <Card key={dim.id} hover={true} className="border-none shadow-sm">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className={`p-3 rounded-xl bg-${dim.color}-50 text-${dim.color}-600`}>
                    <SafeIcon name={dim.icon} className="text-xl" />
                  </div>
                  <div className="text-right">
                    <span className={`text-2xl font-black ${scores[dim.id] < 40 ? 'text-rose-500' : scores[dim.id] < 60 ? 'text-amber-500' : 'text-emerald-500'}`}>
                      {scores[dim.id]}%
                    </span>
                  </div>
                </div>
                <h4 className="font-bold text-slate-900">{dim.name}</h4>
                <div className="mt-4 w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                  <div 
                    className={`h-full bg-${dim.color}-500 transition-all duration-1000`} 
                    style={{ width: `${scores[dim.id]}%` }} 
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Recommended Actions */}
      <Card>
        <CardHeader title="Anbefalte Tiltak" subtitle="Basert på identifiserte risikofaktorer" icon={<SafeIcon name="Target" />} />
        <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recommendations.length > 0 ? recommendations.map((rec, i) => (
            <div key={i} className="p-6 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col">
              <div className="flex justify-between items-start mb-4">
                <span className={`text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-widest ${rec.priority === 'Kritisk' ? 'bg-rose-100 text-rose-600' : 'bg-indigo-100 text-indigo-600'}`}>
                  {rec.priority}
                </span>
              </div>
              <h4 className="font-bold text-slate-900 text-lg mb-2">{rec.title}</h4>
              <p className="text-sm text-slate-500 leading-relaxed flex-1">{rec.desc}</p>
              <Button variant="ghost" size="sm" className="mt-4 p-0 text-indigo-600 font-bold self-start">
                Start prosess →
              </Button>
            </div>
          )) : (
            <div className="col-span-full py-12 text-center text-slate-400">
              <SafeIcon name="CheckCircle" className="text-4xl mx-auto mb-4 text-emerald-500" />
              <p>Ingen kritiske avvik funnet. Fortsett det gode arbeidet!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};